import { InfoCircleOutlined } from '@ant-design/icons';
import { Alert, Button, Form, Modal } from 'antd';
import { useContext, useMemo } from 'react';

import { PictureComponent } from 'app/components/commons/PictureComponent/PictureComponent';
import { HotelContext } from 'app/context/HotelContext/HotelContext';
import { TaskCenterContext } from 'app/context/TaskCenterContext/TaskCenterContext';
import { useRoomBeds } from 'app/hooks/data/core/extranet/room/useRoomBeds';
import { useBedCategories } from 'app/hooks/data/rooms/useBedCategories';
import {
  BABY_COT_BED_CATEGORY_ID,
  DAY_PACKAGE_ROOM_CATEGORY_ID,
} from 'app/typings';
import type { ExtranetHotelRoom } from 'app/typings/core/extranet/hotel/rooms';
import type { ExtranetRoomForm } from 'app/typings/packages';
import { maxRoomCapacity } from 'app/utils/room';

import type { Bed } from '../../../../HotelBuilder/Types';
import { ExtraBed } from '../../ExtraBed';

import styles from './ModernExtraBedModal.module.scss';

type Props = {
  bedTypeId: number;
  rooms: ExtranetHotelRoom[];
  isOpen?: boolean;
  onCompleteTask?: () => void;
};

export const ModernExtraBedModal = ({
  bedTypeId,
  rooms,
  isOpen,
  onCompleteTask,
}: Props) => {
  const [form] = Form.useForm();
  const { hotelId } = useContext(HotelContext);
  const { mutateAsync: saveRoomBeds } = useRoomBeds(hotelId);
  const { data: bedCategories } = useBedCategories();
  const { clearOpenTask } = useContext(TaskCenterContext);

  const bedName =
    bedTypeId === BABY_COT_BED_CATEGORY_ID ? 'baby cot' : 'guest bed';

  const onFinish = async (values: { rooms: ExtranetHotelRoom[] }) => {
    const roomsToSave = {
      rooms: values.rooms.map((room) => ({
        id: room.id,
        beds: room.beds.map((bed) => ({
          id: bed.id,
          categoryId: bed.categoryId,
          isIncluded: bed.included ?? false,
          price: Number(bed.price ?? 0),
        })),
      })),
    };

    await saveRoomBeds(roomsToSave);
    onCompleteTask?.();
    clearOpenTask();
  };

  const getPrefix = (watchedRooms: ExtranetRoomForm[], index: number) => {
    const listBeds = watchedRooms[index].beds;
    const bed = listBeds.find((b: Bed) => b.categoryId === bedTypeId);

    const bedIndex = bed
      ? listBeds.findIndex((b: Bed) => b.id === bed?.id)
      : listBeds.length - 1;

    return [bedIndex];
  };

  const initialValues = useMemo(
    () => ({
      rooms: rooms.map((room) => ({
        ...room,
        beds: room.beds.map((bed: Bed & { isIncluded?: boolean }) => ({
          ...bed,
          included: bed.included ?? bed.isIncluded ?? false,
          price: Number(bed.price ?? 0),
        })),
      })),
    }),
    [rooms]
  );

  const watchedRooms = Form.useWatch('rooms', form) || rooms;

  return (
    <Form
      form={form}
      onFinish={onFinish}
      initialValues={initialValues}
      preserve={false}
    >
      <Modal
        width={558}
        className={styles.extraBedModal}
        bodyStyle={{ maxHeight: 'calc(100vh - 400px)', overflowY: 'auto' }}
        open={isOpen}
        title={
          <div className={styles.extraBedModalHeader}>
            <h1>Can this room accommodate a {bedName}?</h1>
          </div>
        }
        onCancel={clearOpenTask}
        wrapClassName="extraBedModalContainer"
        centered
        footer={[
          <Button size="large" type="ghost" key="close" onClick={clearOpenTask}>
            Close
          </Button>,
          <Button
            key="save"
            size="large"
            type="primary"
            htmlType="submit"
            onClick={() => form.submit()}
          >
            Update
          </Button>,
        ]}
        destroyOnClose
      >
        <div className={styles.extraBedModalBody}>
          <Alert
            type="info"
            message={`Your ${bedName}s will be set up for all your packages`}
            icon={<InfoCircleOutlined />}
            showIcon
          />

          <p className={styles.subtitle}>Your room categories</p>

          <div className={styles.extraBedModalBodyRooms}>
            <Form.List name="rooms">
              {(fields) =>
                fields.map(({ key, name: roomName }, index) => {
                  const room = watchedRooms[index];

                  if (
                    !room ||
                    room.categoryId === DAY_PACKAGE_ROOM_CATEGORY_ID
                  ) {
                    return null;
                  }

                  const baseBeds = room.beds.filter(
                    (bed: Bed) => bed.categoryId !== bedTypeId
                  );

                  const roomCapacity = maxRoomCapacity(
                    baseBeds,
                    bedCategories ?? []
                  );

                  const extraBedCapacity =
                    bedCategories?.find((cat) => cat.id === bedTypeId)
                      ?.capacity || 0;

                  const hasExtraBed = room.beds.some(
                    (bed: Bed) => bed.categoryId === bedTypeId
                  );

                  let capacityDescription = `${roomCapacity} guests`;

                  if (hasExtraBed && bedTypeId === BABY_COT_BED_CATEGORY_ID) {
                    capacityDescription = `${roomCapacity} guests + ${extraBedCapacity} baby`;
                  } else if (hasExtraBed) {
                    capacityDescription = `${
                      roomCapacity + extraBedCapacity
                    } guests`;
                  }

                  return (
                    <div
                      className={styles.extraBedModalBodyRoomsRoom}
                      key={key}
                    >
                      <div className={styles.extraBedModalBodyRoomsRoomInfo}>
                        {room?.coverPicture && (
                          <div
                            className={
                              styles.extraBedModalBodyRoomsRoomInfoImage
                            }
                          >
                            <PictureComponent
                              pictureId={room?.coverPicture.path}
                            />
                          </div>
                        )}

                        <div
                          className={styles.extraBedModalBodyRoomsRoomInfoText}
                        >
                          <p className={styles.title}>
                            {room?.name || room?.categoryName}
                          </p>
                          <p className={styles.description}>
                            {room?.categoryName} · {capacityDescription}
                          </p>
                        </div>
                      </div>

                      <Form.List name={[roomName, 'beds']}>
                        {(_bedsFields, { add, remove }) => (
                          <ExtraBed
                            categoryName={
                              bedTypeId === BABY_COT_BED_CATEGORY_ID
                                ? 'Baby cot'
                                : 'Guest bed'
                            }
                            categoryId={bedTypeId}
                            bed={room?.beds?.find(
                              (bed: Bed) => bed.categoryId === bedTypeId
                            )}
                            listBed={room?.beds || []}
                            add={add}
                            remove={remove}
                            previousPrefixes={['rooms', roomName, 'beds']}
                            prefix={getPrefix(watchedRooms, index)}
                            switchChecked={hasExtraBed}
                          />
                        )}
                      </Form.List>
                    </div>
                  );
                })
              }
            </Form.List>
          </div>
        </div>
      </Modal>
    </Form>
  );
};
